import React, { useState, useMemo, useCallback } from 'react';
import { X, ChevronDown, AlertTriangle, Check } from 'lucide-react';
import http from '../services/httpClient';
import './DayWiseTsrModal.css';

/*
 * Shortage handling is DELEGATED to the parent, not reimplemented here.
 *
 * TodaysSalesReport already owns the Missing Ingredients modal the normal TSR
 * uses — grouped by dish, searchable, with per-ingredient selection and the
 * "Confirm & Create Negative Stock" / "Remove from Recipe" choices. A second
 * shortage popup living in this file would be a worse copy of it that drifts
 * out of step, so this component reports the shortage upward through
 * onShortage() and lets the existing one handle it.
 */

/**
 * Review + import for a multi-date ("day wise") POS export in the normal TSR.
 *
 * Structured after SwiggyOrderReportModal, which solved the same problem for
 * the online lane, with one addition that lane does not need: the duplicate
 * story here has THREE distinct signals, and they are shown separately because
 * they call for different decisions.
 *
 *   already imported   this exact content is in the ledger — nothing to do
 *   prior import       a different version of this day was imported before
 *   existing TSR       ordinary sales already on this date, which is legitimate
 *
 * Props:
 *  - report: the /sales-report/v2/process-tsr response with is_day_wise true
 *  - onClose(): close the modal
 *  - onImported(resultData): after a successful import
 *  - onShortage({ missingIngredients, proceed }): stock is short — hand off to
 *      the shared Missing Ingredients modal. `proceed` re-runs this import with
 *      negative stock approved, and is what the parent calls on confirmation.
 *  - hidden: visually step aside while the parent shows that modal. HIDDEN, not
 *      unmounted — the resume closure and this component's results state have to
 *      survive the handoff, and unmounting would strand both.
 */
export default function DayWiseTsrModal({ report, onClose, onImported, onShortage, hidden = false }) {
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);

  const summary = report?.summary || {};
  const recon = report?.reconciliation || {};

  // Memoised because `|| []` builds a NEW array every render, which would make
  // the memo and callback below recompute on every render — and for a 31-date
  // import that means re-deriving the whole payload each time.
  const dateSummary = useMemo(() => report?.dateSummary || [], [report]);
  const datesPayload = useMemo(() => report?.dates || [], [report]);

  // Already-imported dates start UNCHECKED: re-importing them is the one
  // action guaranteed to be wrong, so it should take a deliberate click.
  const [selected, setSelected] = useState(() => {
    const init = {};
    dateSummary.forEach((d) => { init[d.date] = !d.already_imported; });
    return init;
  });

  const fmtDMY = (d) => {
    const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(d || '');
  };
  const money = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

  const chosen = useMemo(
    () => dateSummary.filter((d) => selected[d.date]),
    [dateSummary, selected]
  );

  const totals = useMemo(() => ({
    dates: chosen.length,
    gross: chosen.reduce((s, d) => s + (d.matched_amount || 0), 0),
    quarantined: chosen.reduce((s, d) => s + (d.unmatched_amount || 0), 0),
    absorbed: chosen.filter((d) => d.absorbed).length,
  }), [chosen]);

  const toggle = (date) => setSelected((p) => ({ ...p, [date]: !p[date] }));
  const setAll = (v) => setSelected(
    Object.fromEntries(dateSummary.map((d) => [d.date, v && !d.already_imported]))
  );

  const buildPayload = useCallback(() => {
    const byDate = new Map(datesPayload.map((d) => [d.date, d]));
    return chosen
      .map((d) => byDate.get(d.date))
      .filter(Boolean)
      .map((d) => ({
        date: d.date,
        salesItems: d.salesItems,
        unmappedItems: d.unmappedItems,
      }));
  }, [chosen, datesPayload]);

  const doImport = useCallback(async (allowNeg) => {
    const dates = buildPayload();
    if (dates.length === 0) { setError('Select at least one date to import.'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const resp = await http.post('/tsr/submit-day-wise', {
        dates,
        allowNegativeStock: allowNeg,
        sourceFile: report?.metadata?.filename || null,
        // Byte identity of the export. One file feeds every date in this import,
        // so each imported date records the same sha — which is what lets a
        // re-upload be told "this file already covered 31 dates" before parsing.
        sourceSha: report?.metadata?.sourceSha || null,
      });
      const data = resp.data || resp;
      if (data.success) {
        setResults(data);
        if (onImported) onImported(data);
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError(err.data?.error || err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, report, onImported]);

  /**
   * Pre-check stock across every selected date before writing anything.
   *
   * A bulk import can be short on an ingredient that no single day would
   * exhaust, and discovering that halfway through leaves some dates in and
   * some out. Aggregating first turns it into one up-front decision.
   */
  const handleSubmit = useCallback(async () => {
    const dates = buildPayload();
    if (dates.length === 0) { setError('Select at least one date to import.'); return; }

    const agg = new Map();
    dates.forEach((d) => d.salesItems.forEach((it) => {
      agg.set(it.menu_item_id, (agg.get(it.menu_item_id) || 0) + it.quantity);
    }));
    const preItems = [...agg.entries()].map(([menuItemId, quantity]) => ({ menuItemId, quantity }));

    setChecking(true);
    setError(null);
    try {
      const resp = await http.post('/total-sales/sales/pre-check-stock', { items: preItems });
      const data = resp.data || resp;
      const missing = (data && data.missingIngredients) || [];
      if (data && data.hasNegativeStock && missing.length > 0) {
        // Gate: nothing is written until the shared modal is confirmed. The
        // pre-check returns itemName/ingredientName/shortage, which is exactly
        // the shape that modal groups and renders — no translation needed.
        setChecking(false);
        if (onShortage) {
          onShortage({ missingIngredients: missing, proceed: () => doImport(true) });
          return;
        }
        // No handler wired: refuse rather than silently importing a shortage.
        setError('Not enough stock for the selected dates. Tick "Allow negative stock" to import anyway.');
        return;
      }
    } catch (err) {
      // A failed pre-check must not block a legitimate import; the per-date
      // transaction still refuses anything it genuinely cannot deduct.
      console.warn('Stock pre-check failed, proceeding:', err?.message || err);
    }
    setChecking(false);
    await doImport(allowNegativeStock);
  }, [buildPayload, doImport, allowNegativeStock, onShortage]);

  const busy = submitting || checking;

  return (
    <>
      <div
        className="dwtsr-overlay"
        style={hidden ? { display: 'none' } : undefined}
        onClick={onClose}
      >
        <div className="dwtsr-container" onClick={(e) => e.stopPropagation()}>

          <div className="dwtsr-head">
            <div>
              <h3 className="dwtsr-title">
                <span className="dwtsr-badge">Day-Wise Sales Report</span>
              </h3>
              <div className="dwtsr-daterange">
                {fmtDMY(summary.first_date)} → {fmtDMY(summary.last_date)}
                {report?.metadata?.filename ? ` · ${report.metadata.filename}` : ''}
              </div>
            </div>
            <button className="dwtsr-close" onClick={onClose} aria-label="Close"><X size={22} /></button>
          </div>

          <div className="dwtsr-chips">
            <span className="dwtsr-chip">{summary.date_count || 0} dates</span>
            <span className="dwtsr-chip">{summary.total_items_sold || 0} units</span>
            <span className="dwtsr-chip">{money(summary.total_revenue)} gross</span>
            <span className="dwtsr-chip">
              {report?.matchSummary?.matched || 0} matched · {report?.matchSummary?.unmatched || 0} not in menu
            </span>
          </div>

          {/* The file carries its own Total row; showing the comparison means a
              silently truncated export is visible rather than merely imported. */}
          {recon.matches === true && (
            <div className="dwtsr-recon ok">
              <Check size={14} /> Reconciles to the file total ({recon.header_qty} units · {money(recon.header_amount)})
            </div>
          )}
          {recon.matches === false && (
            <div className="dwtsr-recon warn">
              <AlertTriangle size={14} />
              File total says {recon.header_qty} units / {money(recon.header_amount)}; parsed
              {' '}{recon.parsed_qty} units / {money(recon.parsed_amount)}. Check the file before importing.
            </div>
          )}
          {(report?.dropped?.length > 0) && (
            <div className="dwtsr-recon warn">
              <AlertTriangle size={14} /> {report.dropped.length} row(s) skipped as unusable
              {' '}({[...new Set(report.dropped.map((d) => d.reason))].join(', ')})
            </div>
          )}

          {error && <div className="dwtsr-error">{error}</div>}

          {results ? (
            <div className="dwtsr-results">
              <div className="dwtsr-results-summary">
                <span className="dwtsr-chip ok">✓ {results.summary.imported} imported</span>
                <span className="dwtsr-chip">↷ {results.summary.skipped} skipped</span>
                {results.summary.errors > 0 && (
                  <span className="dwtsr-chip warn">⚠ {results.summary.errors} errors</span>
                )}
                <span className="dwtsr-chip">{money(results.summary.revenue)} booked</span>
              </div>
              <div className="dwtsr-results-list">
                {results.results.map((r, i) => (
                  <div key={i} className={`dwtsr-result-row ${r.status}`}>
                    <span>{fmtDMY(r.date)}</span>
                    <span className="dwtsr-result-status">
                      {r.status === 'imported'
                        ? `✓ ${money(r.revenue)} · COGS ${money(r.cogs)}${r.absorbed ? ' · absorbed (no deduction)' : ''}`
                        : r.status === 'skipped'
                          ? (r.reason === 'already_imported' ? '↷ already imported' : `↷ ${r.reason}`)
                          : `⚠ ${r.reason}`}
                    </span>
                  </div>
                ))}
              </div>
              <div className="dwtsr-footer">
                <div style={{ flex: 1 }} />
                <button className="dwtsr-submit" onClick={onClose}>Done</button>
              </div>
            </div>
          ) : (
            <>
              <div className="dwtsr-selectbar">
                <button className="dwtsr-link" onClick={() => setAll(true)}>Select all new</button>
                <button className="dwtsr-link" onClick={() => setAll(false)}>Clear</button>
                <div style={{ flex: 1 }} />
                <span className="dwtsr-selcount">
                  {totals.dates} selected · {money(totals.gross)}
                  {totals.quarantined > 0 && ` · ${money(totals.quarantined)} not in menu`}
                </span>
              </div>

              <div className="dwtsr-list">
                {dateSummary.map((d) => {
                  const isOpen = !!expanded[d.date];
                  const payload = datesPayload.find((x) => x.date === d.date);
                  return (
                    <div key={d.date} className={`dwtsr-date ${d.already_imported ? 'imported' : ''}`}>
                      <div className="dwtsr-date-head">
                        <label className="dwtsr-check">
                          <input
                            type="checkbox"
                            checked={!!selected[d.date]}
                            onChange={() => toggle(d.date)}
                          />
                          <span className="dwtsr-date-label">{fmtDMY(d.date)}</span>
                        </label>

                        <span className="dwtsr-date-figures">
                          {d.lines} lines · {d.qty} units · {money(d.gross)}
                        </span>

                        <span className="dwtsr-flags">
                          {d.already_imported && (
                            <span className="dwtsr-flag dup" title="This exact content was already imported">
                              already imported
                            </span>
                          )}
                          {d.has_prior_import && (
                            <span className="dwtsr-flag warn" title="A different version of this date was imported before">
                              prior import
                            </span>
                          )}
                          {d.existing_sales_count > 0 && (
                            <span className="dwtsr-flag info" title="Sales already exist on this date from the normal TSR">
                              {d.existing_sales_count} existing TSR
                            </span>
                          )}
                          {d.absorbed && (
                            <span className="dwtsr-flag absorbed" title="A confirmed closing count already covers this date — revenue is booked but stock is not deducted again">
                              absorbed
                            </span>
                          )}
                          {d.unmatched_qty > 0 && (
                            <span className="dwtsr-flag unmapped">
                              {money(d.unmatched_amount)} not in menu
                            </span>
                          )}
                        </span>

                        <button
                          className="dwtsr-expand"
                          onClick={() => setExpanded((p) => ({ ...p, [d.date]: !p[d.date] }))}
                          aria-label="Toggle items"
                        >
                          <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                        </button>
                      </div>

                      {isOpen && payload && (
                        <div className="dwtsr-items">
                          {payload.salesItems.map((it, i) => (
                            <div key={`m${i}`} className="dwtsr-item">
                              <span>{it.original_name || it.item_name}</span>
                              <span className="dwtsr-item-qty">×{it.quantity}</span>
                              <span className="dwtsr-item-amt">{money(it.amount)}</span>
                            </div>
                          ))}
                          {payload.unmappedItems.map((it, i) => (
                            <div key={`u${i}`} className="dwtsr-item unmapped">
                              <span>{it.item_name} <span className="dwtsr-nim">Not in Menu</span></span>
                              <span className="dwtsr-item-qty">×{it.quantity}</span>
                              <span className="dwtsr-item-amt">{money(it.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="dwtsr-footer">
                <label className="dwtsr-neg">
                  <input
                    type="checkbox"
                    checked={allowNegativeStock}
                    onChange={(e) => setAllowNegativeStock(e.target.checked)}
                  />
                  Allow negative stock
                </label>
                <div style={{ flex: 1 }} />
                <button className="dwtsr-cancel" onClick={onClose} disabled={busy}>Cancel</button>
                <button className="dwtsr-submit" onClick={handleSubmit} disabled={busy || totals.dates === 0}>
                  {checking ? 'Checking stock…' : submitting ? 'Importing…' : `Import ${totals.dates} date(s)`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

    </>
  );
}
